import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { api, unwrap } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Field, RootError, Section } from '@/components/ui/form-controls';
import { Input, NativeSelect } from '@/components/ui/input';
import { applyProblemToForm } from '@/lib/forms';
import { useTenantMutation, type TenantDto } from './api';
import { generalSchema, parseLocales, type GeneralForm } from './schemas';

export function GeneralTab({ tenant }: { tenant: TenantDto }) {
  const { t } = useTranslation();
  const form = useForm<GeneralForm>({
    resolver: zodResolver(generalSchema(t)),
    values: {
      name: tenant.name,
      timezone: tenant.timezone,
      locales: tenant.locales.join(', '),
      residency: tenant.residency,
      status: tenant.status,
    },
  });
  const save = useTenantMutation(
    tenant.id,
    async (v: GeneralForm) =>
      unwrap(
        await api.PATCH('/v1/tenants/{id}', {
          params: { path: { id: tenant.id } },
          body: {
            name: v.name,
            timezone: v.timezone,
            locales: parseLocales(v.locales),
            residency: v.residency,
            status: v.status,
            caps: null,
            modelPolicy: null,
          },
        }),
      ),
    t('admin.common.saved'),
  );

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await save.mutateAsync(v);
    } catch (err) {
      applyProblemToForm(form, err, { fields: ['name', 'timezone', 'locales', 'residency', 'status'] });
    }
  });
  const { errors, isSubmitting, isDirty } = form.formState;

  return (
    <Section title={t('admin.tenantTabs.general')} description={t('admin.general.description')}>
      <form className="grid max-w-xl grid-cols-2 gap-3" onSubmit={(e) => void onSubmit(e)} noValidate>
        <Field className="col-span-2" label={t('admin.tenants.name')} htmlFor="g-name" error={errors.name?.message}>
          <Input id="g-name" aria-invalid={!!errors.name} {...form.register('name')} />
        </Field>
        <Field label={t('admin.tenants.timezone')} htmlFor="g-tz" error={errors.timezone?.message}>
          <Input id="g-tz" aria-invalid={!!errors.timezone} {...form.register('timezone')} />
        </Field>
        <Field label={t('admin.tenants.locales')} htmlFor="g-loc" error={errors.locales?.message}>
          <Input id="g-loc" aria-invalid={!!errors.locales} {...form.register('locales')} />
        </Field>
        <Field label={t('admin.tenants.residency')} htmlFor="g-res" hint={t('admin.general.residencyHint')}>
          <NativeSelect id="g-res" {...form.register('residency')}>
            <option value="any">{t('admin.residency.any')}</option>
            <option value="eu">{t('admin.residency.eu')}</option>
          </NativeSelect>
        </Field>
        <Field label={t('admin.tenants.status')} htmlFor="g-status">
          <NativeSelect id="g-status" {...form.register('status')}>
            {(['active', 'suspended', 'archived'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`admin.tenantStatus.${s}`)}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <div className="col-span-2 flex flex-col gap-2">
          <RootError message={errors.root?.message} />
          <div>
            <Button type="submit" disabled={isSubmitting || !isDirty}>
              {t('admin.common.save')}
            </Button>
          </div>
        </div>
      </form>
    </Section>
  );
}
